import { Link } from 'react-router-dom';

import type { CSSProperties } from 'react';

import { NavIcon } from '@/components/layout/NavIcon';
import { profilesForNavigationItem } from '@/components/layout/navigation';
import { Card, Icon, type IconName } from '@/components/ui';
import { backofficeShortcutsForProfile } from '@/features/backoffice/sections';

import { useAuth } from './providers/authContext';
import styles from './NotFoundRoute.module.css';

/**
 * Catch-all 404, reached by any URL the router does not know, stale bookmarks included. A delivery
 * whose road runs out: the truck drives the known stretch, finds no address, turns round and comes
 * back — a short loop on a screen almost nobody sees, so a little humour costs nothing, and the way
 * back stays the strongest thing on it. Reduced motion: the truck stopped at the end, turned.
 */
interface Shortcut {
  readonly to: string;
  readonly label: string;
  readonly text: string;
  readonly icon?: IconName | undefined;
}

/** "Talvez você procure" (Figma ES1): what a buyer opens most, or an internal profile's stops. */
function shortcutsFor(profile: ReturnType<typeof useAuth>['profile'], buys: boolean): Shortcut[] {
  if (buys) {
    return [
      {
        to: '/#comprar-de-novo',
        label: 'Comprar de novo',
        text: 'Repita o último pedido em um clique',
        icon: 'repeat',
      },
      {
        to: '/pedidos',
        label: 'Meus pedidos',
        text: 'Os pedidos da unidade, por mês',
        icon: 'receipt',
      },
      ...(profile !== null && profilesForNavigationItem('payments').includes(profile)
        ? [
            {
              to: '/pagamentos',
              label: 'Boletos',
              text: 'Os boletos em aberto e pagos',
              icon: 'barcode' as const,
            },
          ]
        : []),
      {
        to: '/notificacoes',
        label: 'Notificações',
        text: 'Boletos e avisos das unidades',
        icon: 'bell',
      },
    ];
  }
  return backofficeShortcutsForProfile(profile)
    .slice(0, 4)
    .map((section) => ({ to: section.to, label: section.label, text: 'Abrir a seção' }));
}

export function NotFoundRoute() {
  const { profile } = useAuth();
  const buys = profile !== null && profilesForNavigationItem('cart').includes(profile);
  const shortcuts = shortcutsFor(profile, buys);

  return (
    <div className={styles.page}>
      <Card elevation="raised" padding="none">
        <div className={styles.root}>
          <div className={styles.scene} aria-hidden="true">
            <div className={styles.card}>
              <div className={styles.head}>
                <span className={styles.headTitle}>Entrega</span>
                <span className={styles.chips}>
                  <span className={`${styles.chip} ${styles.chipGo}`}>A caminho</span>
                  <span className={`${styles.chip} ${styles.chipLost}`}>
                    Endereço não encontrado
                  </span>
                  <span className={`${styles.chip} ${styles.chipBack}`}>Voltando</span>
                </span>
              </div>
              <div className={styles.road}>
                <span className={styles.known} />
                <span className={styles.unknown} />
                <span className={styles.question}>?</span>
                <span className={styles.truck}>
                  <svg viewBox="0 0 30 22" width="24" height="18">
                    <path d="M1 3h17v12H1zM18 7h6l4 4v4H18z" />
                    <circle cx="7" cy="17" r="2.5" />
                    <circle cx="22" cy="17" r="2.5" />
                  </svg>
                </span>
              </div>
              <div className={styles.labels}>
                <span>Enviado</span>
                <span>Separado</span>
                <span>A caminho</span>
                <span className={styles.lostLabel}>Endereço?</span>
              </div>
            </div>
          </div>

          <h1 className={styles.title}>Esta página não existe</h1>
          <p className={styles.description}>
            O endereço acessado não existe ou foi movido.
            {buys ? ' O seu carrinho e o pedido aberto continuam onde estavam.' : ''}
          </p>
          <div className={styles.actions}>
            <Link className={styles.action} to="/" viewTransition>
              {buys ? 'Voltar para Comprar' : 'Voltar ao início'}
            </Link>
            {buys ? (
              <Link className={styles.secondary} to="/pedidos" viewTransition>
                Ver meus pedidos
              </Link>
            ) : null}
          </div>
        </div>
      </Card>
      {shortcuts.length > 0 ? (
        <section className={styles.maybe} aria-labelledby="talvez-procure">
          <h2 id="talvez-procure" className={styles.maybeTitle}>
            Talvez você procure
          </h2>
          <p className={styles.maybeSubtitle}>Atalhos para o que você mais abre</p>
          <ul className={styles.maybeList}>
            {shortcuts.map((shortcut, index) => (
              <li key={shortcut.to} style={{ '--i': index } as CSSProperties}>
                <Link to={shortcut.to} className={styles.maybeCard} viewTransition>
                  <span className={styles.maybeIcon} aria-hidden="true">
                    {shortcut.icon !== undefined ? (
                      <Icon name={shortcut.icon} size={18} />
                    ) : (
                      <NavIcon name={shortcut.to} />
                    )}
                  </span>
                  <strong>{shortcut.label}</strong>
                  <span>{shortcut.text}</span>
                  <span className={styles.maybeOpen}>
                    Abrir <Icon name="chevronRight" size={12} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export default NotFoundRoute;
